import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UITextArea from 'UIComponents/input/UITextArea';
import { useCallback, useMemo, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import { isValidCsatResponse } from './EPCsatResponseUtils';
import styled from 'styled-components';
import Small from 'UIComponents/elements/Small';
import FormattedNumber from 'I18n/components/FormattedNumber';
import UIForm from 'UIComponents/form/UIForm';
import { Text } from 'foundations-components/components/Text';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledNumberButton = styled.button.withConfig({
  displayName: "EpCsatSurveyForm__StyledNumberButton",
  componentId: "sc-1iao4mg-0"
})(props => {
  const {
    theme: _theme,
    selected
  } = props;
  const theme = _theme;
  return {
    height: '44px',
    width: '66px',
    border: `${theme.borderWidth['100']} solid ${selected ? theme.color['border-interactive-pressed'] : theme.color['border-interactive-default']}`,
    borderRadius: theme.borderRadius['100'],
    background: selected ? theme.color['fill-info-subtle'] : 'none'
  };
});
function NumberButton({
  selected,
  onClick,
  children
}) {
  return /*#__PURE__*/_jsx(StyledNumberButton, {
    role: "radio",
    onClick: onClick,
    "aria-checked": selected,
    "aria-labelledby": "ep-csat-survey-score-group",
    selected: selected
    // Default is "submit" which then immediately submits the form
    ,
    type: "button",
    children: children
  });
}
const CsatSurveyFormScoreGrid = styled.div.withConfig({
  displayName: "EpCsatSurveyForm__CsatSurveyFormScoreGrid",
  componentId: "sc-1iao4mg-1"
})(["display:grid;grid-template-columns:repeat(5,66px);width:100%;row-gap:8px;justify-content:space-between;"]);
const UnveilContentWrapper = styled.div.withConfig({
  displayName: "EpCsatSurveyForm__UnveilContentWrapper",
  componentId: "sc-1iao4mg-2"
})(["@keyframes unveil{0%{opacity:0;}100%{opacity:1;}}animation:unveil 300ms ease-out;transition:height 300ms ease-out;overflow:hidden;"]);
const NumberButtonEndLabelWrapper = styled.div.withConfig({
  displayName: "EpCsatSurveyForm__NumberButtonEndLabelWrapper",
  componentId: "sc-1iao4mg-3"
})({
  gridColumnStart: -2,
  display: 'flex'
});
function EPCsatSurveyFormNumbers({
  min,
  max,
  value,
  onSelect,
  highRangeLabel,
  lowRangeLabel
}) {
  const range = useMemo(() => Array.from(Array(max - min + 1).keys()), [max, min]);
  return /*#__PURE__*/_jsx("div", {
    id: "ep-csat-survey-score-group",
    "aria-label": I18n.text('epCsatSurvey.survey.csatQuestion'),
    children: /*#__PURE__*/_jsxs(CsatSurveyFormScoreGrid, {
      role: "radiogroup",
      children: [range.map(idx => {
        const currentNumber = idx + min;
        return /*#__PURE__*/_jsx(NumberButton, {
          selected: currentNumber === value,
          onClick: () => onSelect(currentNumber),
          children: /*#__PURE__*/_jsx(FormattedNumber, {
            value: currentNumber
          })
        }, currentNumber);
      }), /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: lowRangeLabel
      }), /*#__PURE__*/_jsx(NumberButtonEndLabelWrapper, {
        children: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: highRangeLabel
        })
      })]
    })
  });
}
export default function EpCsatSurveyForm({
  updateCsatResponse,
  csatResponse,
  title,
  onDismiss,
  onSubmit
}) {
  const [hasGivenRating, setHasGivenRating] = useState(false);
  const onCsatRatingChange = useCallback(val => {
    setHasGivenRating(true);
    updateCsatResponse({
      score: val
    });
  }, [updateCsatResponse]);
  const onUpdateCanContact = useCallback(e => {
    updateCsatResponse({
      canContact: e.target.checked
    });
  }, [updateCsatResponse]);
  const onUpdateFeedbackMessage = useCallback(e => {
    updateCsatResponse({
      /*  @ts-expect-error Aug-22-2024, 14:7UTC React types upgrade forwards compatibility  */
      feedbackMessage: e.target.value
    });
  }, [updateCsatResponse]);
  const onFormSubmit = useCallback(e => {
    e.preventDefault();
    // Need to validate the form here because the form's keyboard shortcut
    // for submitting will not respect disabled states
    if (!isValidCsatResponse(csatResponse)) {
      return;
    }
    onSubmit();
  }, [csatResponse, onSubmit]);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    children: [/*#__PURE__*/_jsx(CloseButton, {
      color: "icon-primary-disabled",
      onClick: onDismiss
    }), /*#__PURE__*/_jsx(CardSection, {
      paddingInlineEnd: "600",
      children: /*#__PURE__*/_jsxs(UIForm, {
        onSubmit: onFormSubmit,
        children: [/*#__PURE__*/_jsx(H4, {
          children: title !== null && title !== void 0 ? title : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "epCsatSurvey.survey.csatQuestion"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "p-top-3",
          children: /*#__PURE__*/_jsx(EPCsatSurveyFormNumbers, {
            value: csatResponse.score,
            min: 1,
            max: 5,
            lowRangeLabel: /*#__PURE__*/_jsx(Text, {
              as: "span",
              typography: "detail-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "epCsatSurvey.survey.lowRangeLabel"
              })
            }),
            highRangeLabel: /*#__PURE__*/_jsx(Text, {
              as: "span",
              typography: "detail-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "epCsatSurvey.survey.highRangeLabel"
              })
            }),
            onSelect: onCsatRatingChange
          })
        }), hasGivenRating && /*#__PURE__*/_jsxs(UnveilContentWrapper, {
          children: [/*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(Text, {
              as: "span",
              typography: "body-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "epCsatSurvey.survey.feedbackLabel"
              })
            }),
            className: "m-top-3",
            children: /*#__PURE__*/_jsx(UITextArea, {
              placeholder: I18n.text('epCsatSurvey.survey.feedbackPlaceholder'),
              onChange: onUpdateFeedbackMessage,
              value: csatResponse.feedbackMessage
            })
          }), /*#__PURE__*/_jsx(UICheckbox, {
            onChange: onUpdateCanContact,
            value: csatResponse.canContact,
            className: "m-top-4",
            children: /*#__PURE__*/_jsx(Text, {
              as: "span",
              typography: "body-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "epCsatSurvey.survey.canContact"
              })
            })
          })]
        }), /*#__PURE__*/_jsx(UIButton, {
          disabled: !isValidCsatResponse(csatResponse),
          block: true,
          use: "primary",
          className: "m-top-8",
          type: "submit",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "epCsatSurvey.survey.submit"
          })
        })]
      })
    })]
  });
}